import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { MdMore } from "react-icons/md";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import { setPendingServiceOrderId } from "../../redux/slices/pendingService/pendingServiceSlice";
import CancelModal from "../cancelService/CancelModal";
import { reorder } from "../../axios/api/userProfile";
import { navigateTo } from "../../utils/navigationUtils";
import { link } from "../../axios/axiosInstance";

const FALLBACK_IMAGE = "/assets/fallback-service-image.jpg";

const PendingOrdersList = ({ services }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [openActionsId, setOpenActionsId] = useState(null);
  const [showModal, setShowModal] = useState(false);

  const actionsRef = useRef(null);

  const toggleActions = (id) => {
    setOpenActionsId((prev) => (prev === id ? null : id));
  };

  const handleReorderClick = async (id) => {
    const res = await reorder(id);

    if (res?.data?.status === "success") {
      navigateTo(
        navigate,
        `/payment/order/${res.data.serviceId}/${res.data.partnerId}?orderId=${id}`,
      );
    }
  };

  const openCancelModal = (id) => {
    dispatch(setPendingServiceOrderId(id));
    setOpenActionsId(null);
    setShowModal(true);
    document.body.classList.add("isOpenModal");
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (actionsRef.current && !actionsRef.current.contains(e.target)) {
        setOpenActionsId(null);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <>
      <div
        className="max-h-[300px] overflow-y-auto scrollbar
                   flex flex-col divide-y divide-gray-200
                   dark:divide-gray-700"
      >
        {services.map((item) => (
          <div
            key={item.id}
            className="relative flex items-center gap-3 px-3 py-3
                       hover:bg-gray-50 dark:hover:bg-white/5 transition"
          >
            {/* Image */}
            <img
              src={`${link}/${item.image}` || FALLBACK_IMAGE}
              alt={item.service}
              onError={(e) => (e.currentTarget.src = FALLBACK_IMAGE)}
              className="w-16 h-20 md:w-32 md:h-20 rounded-md object-cover object-center flex-shrink-0"
            />

            {/* Columns */}
            <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-12 flex-1 min-w-0 ml-2">
              <span className="text-[15px] font-[500] text-secondary_black dark:text-white truncate min-w-[140px]">
                {t(item.service)}
              </span>

              <span className="text-[15px] text-gray-600 dark:text-white whitespace-nowrap">
                {item.date}
              </span>

              <span className="text-[15px] font-[500] text-secondary_black dark:text-white truncate min-w-[140px]">
                {item.partner}
              </span>
            </div>

            {/* Status */}
            <span className="text-[13px] font-[500] px-4 py-1 rounded-full bg-secondary_black dark:bg-white text-white dark:text-secondary_black whitespace-nowrap">
              {t(item.status === "new" ? "pending" : item.status)}
            </span>

            {/* ⋮ Actions */}
            <div ref={actionsRef} className="relative">
              <MdMore
                onClick={() => toggleActions(item.id)}
                className="cursor-pointer w-6 h-6 fill-primary"
              />

              <AnimatePresence>
                {openActionsId === item.id && (
                  <motion.div
                    initial={{ opacity: 0, x: 6 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 6 }}
                    transition={{ duration: 0.15 }}
                    className="absolute right-full top-[-2.2em] -translate-y-1/2 mr-3
                 w-56 rounded-xl bg-white dark:bg-secondary_black
                 shadow-xl z-50 overflow-hidden"
                  >
                    <button
                      className="w-full text-left px-4 py-3
                   text-secondary_black dark:text-white
                   hover:bg-secondary_black hover:text-white
                   dark:hover:bg-white dark:hover:text-secondary_black
                   transition text-[16px]"
                      onClick={() => handleReorderClick(item.id)}
                    >
                      {t("reorder")}
                    </button>

                    <button
                      onClick={() => openCancelModal(item.id)}
                      className="w-full text-left px-4 py-3
                   text-secondary_black dark:text-white
                   hover:bg-secondary_black hover:text-white
                   dark:hover:bg-white dark:hover:text-secondary_black
                   transition text-[16px]"
                    >
                      {t("cancelOrder")}
                    </button>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        ))}
      </div>

      <CancelModal showModal={showModal} setShowModal={setShowModal} />
    </>
  );
};

export default PendingOrdersList;
